import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Link2, Loader2, PlusCircle, Search } from 'lucide-react';
import { inquiriesAPI, partsAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const scoreTone = (score) => {
  if (score >= 0.95) return 'bg-emerald-100 text-emerald-800';
  if (score >= 0.7) return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
};

export const PartMatchDialog = ({ inquiryId, item, open, onOpenChange, onLinked }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await inquiriesAPI.partSuggestions(inquiryId, item.id);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load part suggestions');
    } finally { setLoading(false); }
  }, [inquiryId, item]);

  useEffect(() => {
    if (open && item) {
      setSearch('');
      setSearchResults([]);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const runSearch = async () => {
    if (!search.trim()) return;
    try {
      const payload = (await partsAPI.search(search.trim())).data;
      const results = payload?.data || payload?.items || payload?.parts || (Array.isArray(payload) ? payload : []);
      setSearchResults(results);
      if (!results.length) toast.info('No matching part found in Part Master');
    } catch { toast.error('Part search failed'); }
  };

  const link = async (partId) => {
    setBusy(partId || 'new');
    try {
      const response = await inquiriesAPI.createItemPart(inquiryId, item.id, partId ? { part_id: partId } : undefined);
      toast.success(`Item linked to ${response.data.internal_part_no}`, {
        description: partId
          ? 'Existing Part Master record reused — its costing history stays intact.'
          : 'New Part Master record created.',
      });
      onOpenChange(false);
      if (onLinked) await onLinked();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not link this item');
    } finally { setBusy(null); }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[96vw] max-w-[1100px] overflow-y-auto" data-testid="part-match-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Link “{item.description}” to Part Master</DialogTitle>
          <DialogDescription className="text-xs">
            Repeat inquiries reuse the existing part so its process flow, costings, quotations and orders stay in one history. Nothing is linked until you choose.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="part-match-loading">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Searching Part Master…
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.length === 0 ? (
              <p className="border border-dashed py-8 text-center text-sm text-muted-foreground" data-testid="part-match-empty">
                No similar existing part found. Create a new Part Master record below.
              </p>
            ) : (
              <div className="space-y-2" data-testid="part-match-suggestions">
                {suggestions.map((suggestion) => (
                  <div key={suggestion.part_id} className="flex flex-col gap-3 border p-4 lg:flex-row lg:items-center" data-testid={`part-suggestion-${suggestion.part_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono font-medium">{suggestion.internal_part_no}</span>
                        <Badge className={scoreTone(suggestion.match_score)} data-testid={`part-suggestion-score-${suggestion.part_id}`}>
                          {Math.round(suggestion.match_score * 100)}% match
                        </Badge>
                        {suggestion.inquiry_count > 0 && <Badge variant="outline">{suggestion.inquiry_count} earlier inquiry(ies)</Badge>}
                      </div>
                      <p className="mt-1 truncate text-sm">{suggestion.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {suggestion.match_reason}
                        {suggestion.customer_part_no ? ` · Customer P/N ${suggestion.customer_part_no}` : ''}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs">
                        {suggestion.active_costing ? (
                          <span className="border px-2 py-1" data-testid={`part-suggestion-costing-${suggestion.part_id}`}>
                            Active costing {suggestion.active_costing.costing_number} · ₹{Number(suggestion.active_costing.selling_price_per_piece || 0).toFixed(2)}/pc
                          </span>
                        ) : <span className="border px-2 py-1 text-muted-foreground">No active costing</span>}
                        {suggestion.last_quotation && (
                          <span className="border px-2 py-1" data-testid={`part-suggestion-quote-${suggestion.part_id}`}>
                            Last quoted {suggestion.last_quotation.quotation_number} · {suggestion.last_quotation.currency} {Number(suggestion.last_quotation.unit_price || 0).toFixed(2)} ({suggestion.last_quotation.status})
                          </span>
                        )}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => link(suggestion.part_id)} disabled={busy === suggestion.part_id} data-testid={`link-suggested-part-${suggestion.part_id}`}>
                      {busy === suggestion.part_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                      Link this part
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t pt-4">
              <p className="mb-2 text-sm font-medium">Search Part Master manually</p>
              <div className="flex gap-2">
                <Input value={search} onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && runSearch()}
                  placeholder="Part number, customer P/N, or description" data-testid="part-match-search-input" />
                <Button variant="outline" onClick={runSearch} data-testid="part-match-search-button">
                  <Search className="mr-2 h-4 w-4" /> Search
                </Button>
              </div>
              {searchResults.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="part-match-search-results">
                  {searchResults.slice(0, 8).map((part) => (
                    <div key={part.id} className="flex items-center gap-3 border p-3" data-testid={`part-search-result-${part.id}`}>
                      <span className="font-mono text-sm font-medium">{part.internal_part_no}</span>
                      <span className="min-w-0 flex-1 truncate text-sm">{part.description}</span>
                      <Button size="sm" variant="outline" onClick={() => link(part.id)} disabled={busy === part.id} data-testid={`link-search-part-${part.id}`}>
                        <CheckCircle2 className="mr-2 h-4 w-4" /> Use this part
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="part-match-cancel">Cancel</Button>
          <Button onClick={() => link(null)} disabled={busy === 'new'} data-testid="create-new-part-from-item">
            {busy === 'new' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />}
            Create new part instead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
